import React, { useState, useEffect, useRef } from 'react';
import { istDateTime } from '../lib/ist';
import axios from 'axios';
import { Download, CheckCircle, Loader2, Package, Monitor, Cpu, Wifi, Printer, FileText, Upload, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth, canEdit } from '../App';

const SoftwareDownload = () => {
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { token, user } = useAuth();
  const editable = canEdit(user, 'booths');
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchInfo = async () => {
    try {
      const res = await axios.get(`${API}/kiosk-download/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDownloadInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch download info:', err);
    }
  };

  const fetchUpdates = async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await axios.get(`${API}/kiosk-updates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpdates(res.data?.items || []);
    } catch (err) {
      console.error('Failed to list update artifacts:', err);
    }
  };

  useEffect(() => {
    fetchInfo();
    fetchUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let okCount = 0, failCount = 0;
    for (const file of files) {
      const form = new FormData();
      form.append('file', file, file.name);
      try {
        await axios.post(`${API}/kiosk-updates/upload`, form, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        okCount++;
      } catch (err) {
        failCount++;
        toast.error(`Upload failed for ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (okCount) toast.success(`Uploaded ${okCount} file${okCount > 1 ? 's' : ''}`);
    setUploading(false);
    await fetchUpdates();
    await fetchInfo();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteArtifact = async (filename) => {
    if (!window.confirm(`Delete ${filename}? Booths will stop seeing this update.`)) return;
    try {
      await axios.delete(`${API}/kiosk-updates/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted ${filename}`);
      await fetchUpdates();
      await fetchInfo();
    } catch (err) {
      toast.error(`Delete failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const humanSize = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const handleDownload = async () => {
    if (!downloadInfo?.package) return;
    setDownloading(true);

    try {
      const pkg = downloadInfo.package;
      const isInstaller = pkg.type === 'installer';

      if (isInstaller) {
        // Direct browser navigation — backend returns 302 to CloudFront,
        // browser follows it as a normal cross-origin navigation (no CORS
        // issue) and saves the file. Using XHR/fetch would block on CORS
        // because CloudFront doesn't return CORS headers for binary
        // downloads. window.location is also better for huge files since
        // the browser streams to disk directly rather than buffering the
        // whole 72MB in JS memory.
        //
        // pkg.download_url already includes the `/api/` prefix (it's the
        // backend's idiomatic URL convention) — and our `API` constant is
        // `BACKEND_URL + '/api'`, so concatenating them would produce a
        // double `/api/api/...`. Use the bare BACKEND_URL here instead.
        const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const url = `${base}${pkg.download_url}`;
        window.location.href = url;
        toast.success('Download started');
      } else {
        // Source-zip fallback flow still uses XHR (needs JWT auth header).
        const url = `${API}${pkg.download_url}`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', 'Piczoo-Booth-Software-windows.zip');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Download started');
      }
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Download failed. Please try again.');
    }
    setDownloading(false);
  };

  const handlePortableDownload = () => {
    const pkg = downloadInfo?.package;
    if (!pkg?.portable_download_url) return;
    // Same direct-navigation trick — backend redirects to CloudFront.
    // Strip the duplicate /api by using BACKEND_URL directly (download_url
    // already starts with /api/).
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    window.location.href = `${base}${pkg.portable_download_url}`;
    toast.success('Portable download started');
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="software-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="font-serif text-2xl sm:text-3xl text-charcoal-900">Booth Software</h1>
        <p className="text-charcoal-800/60 mt-1 text-sm sm:text-base">
          Install on the photobooth PC. Pair with a booth from the Booths tab.
        </p>
      </div>

      {/* Main download card */}
      {downloadInfo?.package ? (
        <div className="bg-gradient-to-br from-charcoal-900 to-charcoal-800 rounded-2xl p-5 sm:p-6 text-white mb-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Package className="w-6 h-6 sm:w-7 sm:h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-serif text-lg sm:text-xl">{downloadInfo.package.name}</h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 font-mono" data-testid="software-version-badge">
                  v{downloadInfo.package.semver || downloadInfo.package.version}
                </span>
                {downloadInfo.package.build_tag && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60 font-mono" data-testid="software-build-tag" title="Build tag — changes on every deploy. Compare with the version shown in your booth's title bar to verify you have the latest.">
                    build {downloadInfo.package.build_tag}
                  </span>
                )}
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-300">
                  Production Ready
                </span>
              </div>
              <p className="text-xs sm:text-sm mt-1 text-white/70">
                {downloadInfo.package.description}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {downloadInfo.package.features?.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-white/70">
                <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                <span>{f}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10 mb-4">
            {downloadInfo.package.requirements?.map((req, i) => (
              <div key={i} className="text-xs text-white/50 bg-white/5 px-2.5 py-1 rounded">
                {req}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-white/40">
              {downloadInfo.package.size || (downloadInfo.package.size_bytes ? humanSize(downloadInfo.package.size_bytes) : '')}
              {downloadInfo.package.type === 'installer' && (
                <span className="ml-2 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono text-[10px]">
                  one-click .exe · auto-update enabled
                </span>
              )}
            </span>
            <div className="flex gap-2">
              {downloadInfo.package.portable_download_url && (
                <button
                  onClick={handlePortableDownload}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium bg-white/10 hover:bg-white/20 text-white text-sm transition-colors"
                  data-testid="download-portable-btn"
                  title="Single .exe — no install, no admin needed. Good for borrowed/locked-down laptops."
                >
                  <Download className="w-4 h-4" />
                  Portable .exe
                </button>
              )}
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium bg-rose-500 hover:bg-rose-600 disabled:bg-rose-400 text-white text-sm transition-colors"
                data-testid="download-software-btn"
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    {downloadInfo.package.type === 'installer' ? 'Download Setup .exe' : 'Download for Windows'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="card-dashboard mb-6 animate-pulse h-40" />
      )}

      {/* Printer tools — DNP PrinterInfo (hardware media counter readout) */}
      <div className="card-dashboard mb-6" data-testid="printer-tools-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Printer className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-serif text-lg text-charcoal-900">DNP PrinterInfo Tool</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-mono">v1.2.1.1</span>
            </div>
            <p className="text-sm text-charcoal-800/60 mt-1">
              Install this on the booth PC so the kiosk app (v3.14+) can read the printer's own
              paper counter — exact prints remaining, straight from the ribbon RFID chip.
              Supports DS40, DS80, DS620/620A and RX1 series.
            </p>
            <ol className="text-xs text-charcoal-800/50 mt-2 space-y-0.5 list-decimal list-inside">
              <li>Download &amp; unzip on the booth PC, run the setup .exe (needs admin, installs .NET if missing)</li>
              <li>Open PrinterInfo once while the printer is idle — confirm "Media Remaining" shows a number</li>
              <li>In the booth's Inventory tab, hit "Read counter now" to verify the kiosk picks it up</li>
            </ol>
          </div>
          <a
            href={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api/tools/printerinfo`}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium bg-purple-600 hover:bg-purple-700 text-white text-sm transition-colors flex-shrink-0"
            data-testid="download-printerinfo-btn"
          >
            <Download className="w-4 h-4" />
            Download (755 KB)
          </a>
        </div>
      </div>

      {/* Super-admin: publish new build (auto-update feed) */}
      {isSuperAdmin && (
        <div className="card-dashboard mb-6" data-testid="publish-build-panel">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Upload className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-charcoal-900">Publish new build (auto-update feed)</h3>
              <p className="text-sm text-charcoal-800/70 mt-1">
                Upload the artifacts from <code className="bg-cream-100 px-1.5 py-0.5 rounded text-xs">kiosk-electron/dist/</code> after running{' '}
                <code className="bg-cream-100 px-1.5 py-0.5 rounded text-xs">npm run build</code> on a Windows machine. Every booth
                checks for updates on launch + every hour. Required files:{' '}
                <code className="bg-cream-100 px-1 py-0.5 rounded text-[11px]">latest.yml</code>,{' '}
                <code className="bg-cream-100 px-1 py-0.5 rounded text-[11px]">Piczoo-Kiosk-Setup-X.Y.Z.exe</code>,{' '}
                <code className="bg-cream-100 px-1 py-0.5 rounded text-[11px]">.exe.blockmap</code>. Optional:{' '}
                <code className="bg-cream-100 px-1 py-0.5 rounded text-[11px]">Piczoo-Kiosk-Portable-X.Y.Z.exe</code>.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            {editable && (
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".exe,.yml,.blockmap"
              onChange={(e) => handleUploadFiles(Array.from(e.target.files || []))}
              disabled={uploading}
              className="text-sm file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-rose-500 file:text-white file:font-medium file:cursor-pointer hover:file:bg-rose-600 disabled:file:bg-rose-400"
              data-testid="upload-build-input"
            />
            )}
            <button
              onClick={fetchUpdates}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cream-100 hover:bg-cream-200 text-charcoal-900 text-sm"
              data-testid="refresh-updates-btn"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            {uploading && (
              <span className="flex items-center gap-1.5 text-sm text-charcoal-800/60">
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
              </span>
            )}
          </div>

          {updates.length > 0 ? (
            <div className="border border-cream-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-cream-50 text-charcoal-800/60 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2">Filename</th>
                    <th className="text-left px-3 py-2">Size</th>
                    <th className="text-left px-3 py-2">Modified</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {updates.map((u) => (
                    <tr key={u.filename} className="border-t border-cream-100" data-testid={`update-row-${u.filename}`}>
                      <td className="px-3 py-2 font-mono text-xs">{u.filename}</td>
                      <td className="px-3 py-2 text-charcoal-800/70">{humanSize(u.size)}</td>
                      <td className="px-3 py-2 text-charcoal-800/60 text-xs">
                        {istDateTime(u.modified)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {editable && (
                        <button
                          onClick={() => handleDeleteArtifact(u.filename)}
                          className="text-rose-500 hover:text-rose-700"
                          data-testid={`delete-update-${u.filename}`}
                          title="Delete this artifact"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-charcoal-800/50 italic">
              No update artifacts published yet. Booths will fall back to the source-zip flow.
            </p>
          )}
        </div>
      )}

      {/* Setup instructions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <div className="card-dashboard">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
            <Cpu className="w-5 h-5 text-amber-600" />
          </div>
          <h3 className="font-medium text-charcoal-900 mb-2">1. Install prerequisites</h3>
          <p className="text-sm text-charcoal-800/70 mb-2">
            On the booth PC, install these <strong>before</strong> running the software:
          </p>
          <ul className="text-sm text-charcoal-800/70 list-disc pl-5 space-y-1">
            <li>
              <a href="https://nodejs.org/" target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:underline">
                Node.js 20 LTS
              </a> &mdash; required (click the big green LTS button on nodejs.org)
            </li>
            <li>DNP <strong>DS-RX1</strong> printer driver (run the installer as admin, then reboot)</li>
          </ul>
        </div>

        <div className="card-dashboard">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
            <Monitor className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="font-medium text-charcoal-900 mb-2">2. Run the booth software</h3>
          <p className="text-sm text-charcoal-800/70">
            Unzip the downloaded folder. Double-click
            <code className="bg-cream-100 px-1.5 py-0.5 mx-1 rounded text-xs">setup.bat</code>
            &mdash; it installs dependencies and launches the booth automatically.
            (Or run <code className="bg-cream-100 px-1.5 py-0.5 mx-1 rounded text-xs">npm install</code> then <code className="bg-cream-100 px-1.5 py-0.5 mx-1 rounded text-xs">npm start</code> manually.)
          </p>
        </div>

        <div className="card-dashboard">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mb-3">
            <Wifi className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="font-medium text-charcoal-900 mb-2">3. Pair with a booth</h3>
          <p className="text-sm text-charcoal-800/70">
            In the 3-step wizard, point the backend URL at <span className="font-mono">https://piczoo.in</span>,
            pick the DS-RX1 printer, then enter the pairing code from the Booths page.
          </p>
        </div>
      </div>

      {/* Print spec card */}
      <div className="card-dashboard mb-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Printer className="w-5 h-5 text-rose-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-charcoal-900">Print specification</h3>
            <p className="text-sm text-charcoal-800/70 mb-3">
              Fixed 4×6&quot; layout — two identical 2×6&quot; strips per print, each with 4 photos and the
              venue&apos;s brand logo + the Piczoo wordmark at the bottom. 700 prints per DNP roll.
            </p>
            <a
              href={`${API}/download/dnp-driver`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dnp-driver-download"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 text-sm font-medium"
            >
              <Download className="w-4 h-4" /> Download DS-RX1 driver v1.13.0 (.exe)
            </a>
            <p className="text-[11px] text-charcoal-800/50 mt-2">
              Standard fleet-wide driver. Install on the booth PC <strong>before</strong> running the kiosk software for the first time.
            </p>
          </div>
        </div>
      </div>

      {/* Exit / kiosk-mode notes */}
      <div className="card-dashboard">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-medium text-charcoal-900 mb-2">Operating notes</h3>
            <ul className="text-sm text-charcoal-800/70 space-y-1.5 list-disc pl-5">
              <li>Booth runs full-screen. A small, faded <strong>×</strong> in the top-right corner is the only exit affordance.</li>
              <li>Tapping × prompts for the booth&apos;s admin password (configurable in Booths → Settings).</li>
              <li>Live config sync — colour palette, idle logo, pricing all push to the booth in real time.</li>
              <li>If the network drops, photos are cached locally and uploaded when the connection returns.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoftwareDownload;
